<template>
  <template v-for="orderLine in order.orderLines" :key="orderLine.id">
    <RevButton
      v-if="['NEW', 'PAID'].includes(orderLine.state.code)"
      class="w-full"
      :data-qa="`confirm-${orderLine.id}`"
      size="small"
      variant="secondary"
      @click="handleConfirm(orderLine.id)"
    >
      {{
        order.orderLines.length > 1
          ? i18n(translations.confirmOrderLine, { orderLineId: orderLine.id })
          : i18n(translations.confirm)
      }}
    </RevButton>

    <RevButton
      v-if="
        orderLine.state.code === 'SHIPPED' &&
        orderLine.useImei &&
        orderLine.useItemIdentifier &&
        !orderLine.itemIdentifier
      "
      class="w-full"
      :data-qa="`add-order-line-imei-${orderLine.id}`"
      size="small"
      variant="secondary"
      @click="handleAddOrderLineIMEI(orderLine.id)"
    >
      {{ i18n(translations.addOrderLineIMEI, { orderLineId: orderLine.id }) }}
    </RevButton>

    <!-- Links to the Zendesk-based conversation -->
    <RevButton
      v-if="
        orderLine.sav?.id &&
        orderLine.sav?.action?.code === 'REPLY' &&
        hasRouteAccess(
          {
            name: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
            params: { customerRequestId: orderLine.sav.id },
          },
          PERMISSION_TYPES.EDIT,
        )
      "
      class="w-full"
      :data-qa="`reply-to-customer-${orderLine.id}`"
      size="small"
      :to="{
        name: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
        params: { customerRequestId: orderLine.sav.id },
      }"
      variant="secondary"
    >
      <div class="leading-none">
        {{ orderLine.sav?.action?.label }}
        <div v-if="order.orderLines.length > 1">
          {{
            i18n(translations.aboutLine, {
              orderLineId: orderLine.id,
            })
          }}
        </div>
      </div>
    </RevButton>

    <RevButton
      v-if="
        orderLine.sav?.id &&
        orderLine.sav?.action?.code === 'VIEW' &&
        hasRouteAccess(
          {
            name: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
            params: { customerRequestId: orderLine.sav.id },
          },
          PERMISSION_TYPES.EDIT,
        )
      "
      class="w-full"
      :data-qa="`go-to-conversation-${orderLine.id}`"
      :icon="IconContact"
      size="small"
      :to="{
        name: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
        params: { customerRequestId: orderLine.sav.id },
      }"
      variant="primary"
    >
      <div class="leading-none">
        {{ orderLine.sav?.action?.label }}
        <div v-if="order.orderLines.length > 1">
          {{
            i18n(translations.aboutLine, {
              orderLineId: orderLine.id,
            })
          }}
        </div>
      </div>
    </RevButton>

    <!-- Links to the Intercom-based conversation -->
    <RevButton
      v-if="shouldDisplayRequestButton(orderLine, 'REPLY')"
      class="w-full"
      :data-qa="`reply-to-customer-${orderLine.publicId}`"
      size="small"
      :to="{
        name: ROUTE_NAMES.CUSTOMER_CARE.REQUEST,
        params: { orderlinePublicId: orderLine.publicId },
      }"
      variant="secondary"
    >
      <div class="leading-none">
        {{ orderLine.helpRequest?.action?.label }}
        <div v-if="order.orderLines.length > 1">
          {{
            i18n(translations.aboutLine, {
              orderLineId: orderLine.id,
            })
          }}
        </div>
      </div>
    </RevButton>

    <RevButton
      v-if="shouldDisplayRequestButton(orderLine, 'VIEW')"
      class="w-full"
      :data-qa="`go-to-intercom-conversation-${orderLine.publicId}`"
      :icon="IconContact"
      size="small"
      :to="{
        name: ROUTE_NAMES.CUSTOMER_CARE.REQUEST,
        params: { orderlinePublicId: orderLine.publicId },
      }"
      variant="primary"
    >
      <div class="leading-none">
        {{ orderLine.helpRequest?.action?.label }}
        <div v-if="order.orderLines.length > 1">
          {{
            i18n(translations.aboutLine, {
              orderLineId: orderLine.id,
            })
          }}
        </div>
      </div>
    </RevButton>

    <AddOrderLineIMEIDrawer :orderLine :refreshOrders />
  </template>
</template>

<script setup lang="ts">
import { usePermissions } from '#imports'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { openModal } from '@ds/components/ModalBase'
import { IconContact } from '@ds/icons/IconContact'

import { PERMISSION_TYPES } from '~/constants/permissions'
import { ROUTE_NAMES } from '~/constants/routes'

import type { Order } from '../../../../../../Tab.types'

import translations from './OrderLinesActions.translations'
import { MODAL_NAME as ADD_ORDERLINE_IMEI_DRAWER } from './components/AddOrderLineIMEIDrawer/AddOrderLineIMEIDrawer.constants'
import AddOrderLineIMEIDrawer from './components/AddOrderLineIMEIDrawer/AddOrderLineIMEIDrawer.vue'
import { useConfirmOrderLines } from './composables/useConfirmOrderLines'

const i18n = useI18n()
const { hasRouteAccess } = usePermissions()
const { confirmOrderLine } = useConfirmOrderLines()
const props = defineProps<{ order: Order; refreshOrders: () => void }>()

async function handleConfirm(orderLineId: number) {
  await confirmOrderLine(orderLineId, props.refreshOrders)
}

function handleAddOrderLineIMEI(orderLineId: number) {
  openModal(ADD_ORDERLINE_IMEI_DRAWER + orderLineId)
}

function shouldDisplayRequestButton(
  orderLine: Order['orderLines'][0],
  actionType: 'VIEW' | 'REPLY',
) {
  return (
    orderLine.publicId &&
    orderLine.helpRequest?.action?.code === actionType &&
    hasRouteAccess(
      {
        name: ROUTE_NAMES.CUSTOMER_CARE.REQUEST,
        params: { orderlinePublicId: orderLine.publicId },
      },
      PERMISSION_TYPES.EDIT,
    )
  )
}
</script>
